import * as apiHelper from './ApiHelper'
import axios from 'axios'
import { getDomain } from 'utils'
import { getMyIp } from '../services/ip'

const API_PATH = '/api';
const PARTICIPANT_PATH = `${API_PATH}/participant`;
const PROFILE_PATH = `${API_PATH}/participant`;
const MATCHING_PATH = `${API_PATH}/matching`;
const QUESTIONS_PATH = `${API_PATH}/question`;
const SSE_PATH = `${API_PATH}/sse`;
const ADMIN_PATH = `${API_PATH}/admin`;
const EVENT_PATH = `${API_PATH}/event`;
const VACANCY_PATH = `${API_PATH}/vacancies`;
const MENTORSHIP_PATH = `${API_PATH}/mentorship`;
const ONBOARDING_PATH = `${API_PATH}/onboarding`;
const HOST = process.env.REACT_APP_API_HOST;

export const voleeApi = {
    auth: {
        login: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/login`, params, body),
        loginGoogle: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/login/google`, params, body),
        registerAdmin: (body, secret) => apiHelper.post(`${HOST}${API_PATH}/auth/register-admin`, { secret }, body),
        registerUser: (body) => apiHelper.post(`${HOST}${API_PATH}/auth/register-user`, null, body),
        recovery: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/recovery`, params, body),
        confirmRecovery: (body, params) => {
            return apiHelper.post(`${HOST}${API_PATH}/auth/confirm-recovery`, params, body)
        },
        resendRecoveryEmail: () => apiHelper.post(`${HOST}${API_PATH}/auth/confirm-email`),
        magicAuth: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/auth-magic`, params, body),
        agreePolicy: () => apiHelper.post(`${HOST}${API_PATH}/auth/agree-policy`),
        otp: {
            precheck: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/otp/precheck`, params, body),
            challenge: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/otp/challenge`, params, body),
            validate: (body, params) => apiHelper.post(`${HOST}${API_PATH}/auth/otp/validate`, params, body),
        },
        acceptInvite: (inviteEncoded) => apiHelper.post(`${HOST}${API_PATH}/invitation/${inviteEncoded}`)
    },
    users: {
        delete: (userId, body) => apiHelper.del(`${HOST}${API_PATH}/users/delete/${userId}`, null, body),
    },
    community: {
        create: (body, params) => apiHelper.post(`${HOST}${API_PATH}/community/create`, params, body),
        get: (communityId) => apiHelper.get(`${HOST}${API_PATH}/community/get-by-id`, {
            communityId,
        }),
        getByDomain: (domain) => apiHelper.get(`${HOST}${API_PATH}/community/get-by-domain`, { domain }),
        getByUserId: (userId) => apiHelper.get(`${HOST}${API_PATH}/community/get-by-user-id`, { userId }),
        participants: (communityId, page, search, cityTagKeys) =>
            apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants-list`, {
                page,
                search,
                ...(cityTagKeys?.length ? { cityTagKeys: cityTagKeys.join(',') } : {}),
            }),
        /** Same as participants(), but list is ordered by instant-matching relevance for the current user. */
        participantsByRelevance: (communityId, page, search, cityTagKeys) =>
            apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants-list/by-relevance`, {
                page,
                search,
                ...(cityTagKeys?.length ? { cityTagKeys: cityTagKeys.join(',') } : {}),
            }),
        participantsPreview: (communityId, page, search) =>
            apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/preview/participants`, { page, search }),
        participantsMap: (communityId) =>
            apiHelper.get(`${HOST}${API_PATH}/participant/${communityId}/participants-with-location`),
        participantsAdmin: (communityId, page, search) =>
            apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants`, { page, search }),
        charts: (communityId) => apiHelper.get(`${HOST}${API_PATH}/charts/data`, { communityId }),
        options: (communityId) => apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/options`),
        faq: {
            get: (communityId) => apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/faq`),
            save: (communityId, body) => apiHelper.put(`${HOST}${API_PATH}/community/${communityId}/faq`, null, body),
        },
        conferenceButtons: {
            get: (communityId) => apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/conference-buttons`),
            save: (communityId, body) => apiHelper.put(`${HOST}${API_PATH}/community/${communityId}/conference-buttons`, null, body),
        },
        distributions: (communityId) => apiHelper.get(`${HOST}${API_PATH}/charts/distributions`, { communityId }),
        notifications: {
            notifyAllViaEmail: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify`, null, body),
            notifyAllViaTelegram: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/telegram`, null, body),
            recommendContactsViaEmail: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/recommended-contacts`, null, body),
            recommendContactsViaTelegram: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/telegram/recommended-contacts`, null, body),
            telegramGroupNotification: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/telegram/group/image`, null, body),
            notifyEventViaTelegram: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/telegram/event`, null, body),
            notifyEventParticipantsViaEmail: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/event`, null, body),
            notifyEventParticipantsViaTelegram: (communityId, body) => apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/telegram/event/general`, null, body),
            uploadAsset: (communityId, file) => {
                const formData = new FormData();
                formData.append('image', file);
                // Omit JSON content-type so axios sets multipart boundary
                return apiHelper.post(
                    `${HOST}${API_PATH}/community/${communityId}/participants/notify/assets`,
                    null,
                    formData,
                    { 'Content-Type': undefined }
                );
            },
            preview: (communityId, body) =>
                apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/preview`, null, body),
            listMailings: (communityId) =>
                apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings`),
            createMailing: (communityId, body) =>
                apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings`, null, body),
            updateMailing: (communityId, id, body) =>
                apiHelper.put(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings/${id}`, null, body),
            getMailing: (communityId, id) =>
                apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings/${id}`),
            deleteMailing: (communityId, id) =>
                apiHelper.del(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings/${id}`),
            duplicateMailing: (communityId, id) =>
                apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings/${id}/duplicate`),
            sendMailing: (communityId, id) =>
                apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/mailings/${id}/send`),
            listSends: (communityId) =>
                apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants/notify/sends`),
            getSend: (communityId, id) =>
                apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants/notify/sends/${id}`),
            listRecipients: (communityId, id, params) =>
                apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants/notify/sends/${id}/recipients`, params),
            deleteSend: (communityId, id) =>
                apiHelper.del(`${HOST}${API_PATH}/community/${communityId}/participants/notify/sends/${id}`),
            duplicateSend: (communityId, id) =>
                apiHelper.post(`${HOST}${API_PATH}/community/${communityId}/participants/notify/sends/${id}/duplicate`),
        },
        topTags: (communityId) => apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/top-tags`),
        locations: (communityId) => apiHelper.get(`${HOST}${API_PATH}/community/${communityId}/participants/locations`),
    },
    onboarding: {
        questions: (communityId, lang) =>
            apiHelper.get(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/questions`, lang ? { lang } : undefined),
        progress: (communityId) =>
            apiHelper.get(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/progress`),
        start: (communityId) =>
            apiHelper.post(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/start`),
        saveAnswer: (communityId, questionKey, value) =>
            apiHelper.put(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/answers/${questionKey}`, null, value),
        complete: (communityId) =>
            apiHelper.post(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/complete`),
        saveProfileStep: (communityId, body) =>
            apiHelper.put(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/profile-steps`, null, body),
        skipQuestions: (communityId) =>
            apiHelper.post(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/skip-questions`),
        finish: (communityId, headers) =>
            apiHelper.post(`${HOST}${ONBOARDING_PATH}/communities/${communityId}/finish`, null, {}, headers),
    },
    contact: {
        get: () => apiHelper.get(`${HOST}${API_PATH}/contact`)
    },
    general: {
        init: (body, params) => apiHelper.post(`${HOST}${API_PATH}/init`, params, body, null),
        getHost: () => { return HOST },
        currentUser: () => apiHelper.get(`${HOST}/current-user`),
        currentParticipant: (communityId) => apiHelper.get(`${HOST}/current-participant`, { communityId }),
        saveThemePreference: (themePreference) => apiHelper.put(`${HOST}/current-user/theme`, null, { themePreference }),
        saveLangChoice: (lang) => apiHelper.post(`${HOST}/current-user/lang`, null, lang, {
            "Content-Type": "text/plain"
        }),
        subscribeToWebPush: (subscription) => apiHelper.post(`${HOST}/current-user/web-push`, null, subscription),
        notifications: {
            get: (params) => apiHelper.get(`${HOST}${API_PATH}/notifications`, params),
            seen: (ids) => apiHelper.post(`${HOST}${API_PATH}/notifications/seen`, null, ids),
            dismiss: (ids) => apiHelper.post(`${HOST}${API_PATH}/notifications/dismiss`, null, ids),
            dismissAll: () => apiHelper.post(`${HOST}${API_PATH}/notifications/dismiss-all`),
            hasNewerThan: (mostResentId) => apiHelper.get(`${HOST}${API_PATH}/notifications/${mostResentId}/newer`),
        }
    },
    participant: {
        create: (body, userId, headers) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/create`, { userId }, body, headers),
        myAnswers: (participantId) => apiHelper.get(`${HOST}${PARTICIPANT_PATH}/my-answers`, { participantId }),
        updateAnswers: (participantId, newAnswers, params = {}) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/update-answers`, { participantId, ...params }, newAnswers),
        myCommunities: (userId) => apiHelper.get(`${HOST}${PARTICIPANT_PATH}/my-communities`, { userId }),
        getProfile: (participantId) => apiHelper.get(`${HOST}${PARTICIPANT_PATH}/profile`, { participantId }),
        resolveSlug: (slug) => apiHelper.get(`${HOST}${PARTICIPANT_PATH}/profile/${slug}`),
        updateLookingFor: (participantId, lookingForText) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/update-looking-for`, {
            participantId,
        }, lookingForText),
        updateProfile: (profile) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/update-profile`, null, profile),
        updateNetworkingPref: (participantId, networkingEnabled) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/update-networking-pref`, { participantId, networkingEnabled }),
        updateNotificationPref: (contactType, disable) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/update-notification-pref`, { contactType, disable }),
        linkedInSearch: (linkedInProfileUrl) => apiHelper.get(`${HOST}${PARTICIPANT_PATH}/search-linkedin`, { linkedInProfileUrl }),
        telegramConnectionSSE: (userId) => apiHelper.sseEventSource(`${HOST}${SSE_PATH}/telegram-connect?userId=${userId}`),
        tutorialSeen: (participantId) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/tutorial-seen`, { participantId }),
        updateLanguage: (communityId, language) => apiHelper.post(`${HOST}${PARTICIPANT_PATH}/update-language`, null, { communityId, language }),
    },
    event: {
        list: (filters) => {
            const params = {
                communityId: filters.communityId,
                page: filters.page || 0,
                size: filters.size || 10,
            };
            if (filters.dateFilter) params.dateFilter = filters.dateFilter;
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            if (filters.city) params.city = filters.city;
            if (filters.country) params.country = filters.country;
            if (filters.eventType) params.eventType = filters.eventType;
            if (filters.organizer) params.organizer = true;
            if (filters.createdByMe) params.createdByMe = true;
            if (filters.lang) params.lang = filters.lang;
            return apiHelper.get(`${HOST}${EVENT_PATH}/list`, params);
        },
        cities: (communityId, search) => {
            const params = { communityId };
            if (search) params.search = search;
            return apiHelper.get(`${HOST}${EVENT_PATH}/cities`, params);
        },
        countries: (communityId, search) => {
            const params = { communityId };
            if (search) params.search = search;
            return apiHelper.get(`${HOST}${EVENT_PATH}/countries`, params);
        },
        past: (communityId) => apiHelper.get(`${HOST}${EVENT_PATH}/past`, { communityId }),
        upcoming: (communityId) => apiHelper.get(`${HOST}${EVENT_PATH}/upcoming`, { communityId }),
        map: (communityId) => apiHelper.get(`${HOST}${EVENT_PATH}/map`, { communityId }),
        search: (communityId, searchTerm) => apiHelper.get(`${HOST}${EVENT_PATH}/search`, { communityId, searchTerm }),
        getDetails: (eventSlug, lang) => {
            const params = lang ? { lang } : {};
            return apiHelper.get(`${HOST}${EVENT_PATH}/${eventSlug}`, params);
        },
        get: (eventId, params = {}) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}`, params),
        update: (eventId, body) => apiHelper.patch(`${HOST}${EVENT_PATH}/${eventId}`, null, body),
        create: (body) => apiHelper.post(`${HOST}${EVENT_PATH}/create`, null, body),
        checkSlugAvailability: (slug) => apiHelper.get(`${HOST}${EVENT_PATH}/slug/available`, { slug }),
        uploadImage: (eventId, file) => {
            const formData = new FormData();
            formData.append('image', file);
            // Don't set Content-Type header - let axios set it automatically with boundary
            return apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/upload-image`, null, formData);
        },
        getConfirmedParticipants: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/participants/confirmed`), // Added for consistency
        confirmParticipation: (eventId, utmAttribution) =>
            apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/confirm-participation`, null, utmAttribution || null),
        cancelParticipation: (eventId) => apiHelper.patch(`${HOST}${EVENT_PATH}/${eventId}/cancel-participation`), // Added for consistency
        getParticipationStatus: (participantId, eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/participations/${participantId}/${eventId}`), // Added for consistency
        // TODO: Add approve/reject endpoints when defined
        approveParticipant: (eventId, participantId) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/participants/${participantId}/approve`), // Placeholder/Assumption
        rejectParticipant: (eventId, participantId) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/participants/${participantId}/reject`), // Placeholder/Assumption
        setParticipantStatus: (eventId, participantId, status) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/participant/${participantId}/status/${status}`),
        getAllParticipants: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/participants/confirmed`),
        getParticipantsWithTelegram: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/participants/with-telegram`),
        getParticipantsForEmailNotification: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/participants/for-email-notification`),
        searchParticipants: (eventId, { mode, q, page, size } = {}) =>
            apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/participants/search`, { mode, q, page, size }),
        live: {
            audience: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/live`),
            manage: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/live/manage`),
            present: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/live/present`),
            updateSettings: (eventId, body) => apiHelper.patch(`${HOST}${EVENT_PATH}/${eventId}/live`, null, body),
            askQuestion: (eventId, body) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/questions`, null, body),
            voteQuestion: (eventId, questionId, body) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/questions/${questionId}/vote`, null, body),
            deleteQuestion: (eventId, questionId) =>
                apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/live/questions/${questionId}`),
            moderateQuestion: (eventId, questionId, body) =>
                apiHelper.patch(`${HOST}${EVENT_PATH}/${eventId}/live/questions/${questionId}`, null, body),
            presentQuestion: (eventId, questionId) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/questions/${questionId}/present`),
            clearPresentedQuestion: (eventId, questionId) =>
                apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/live/questions/${questionId}/present`),
            createPoll: (eventId, body) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/polls`, null, body),
            launchPoll: (eventId, pollId) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/polls/${pollId}/launch`),
            closePoll: (eventId, pollId) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/polls/${pollId}/close`),
            patchPoll: (eventId, pollId, body) =>
                apiHelper.patch(`${HOST}${EVENT_PATH}/${eventId}/live/polls/${pollId}`, null, body),
            deletePoll: (eventId, pollId) =>
                apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/live/polls/${pollId}`),
            respondPoll: (eventId, pollId, body) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/live/polls/${pollId}/responses`, null, body),
        },
        notifications: {
            previewEmail: (eventId, body) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/notifications/email/preview`, null, body),
            sendEmail: (eventId, body) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/notifications/email/send`, null, body),
            sendTelegram: (eventId, body) =>
                apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/notifications/telegram/send`, null, body),
            listSends: (eventId) =>
                apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/notifications/sends`),
            listRecipients: (eventId, sendId, params) =>
                apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/notifications/sends/${sendId}/recipients`, params),
        },
        delete: (eventId) => apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}`),
        photos: {
            // Upload multiple photos
            upload: (eventId, files) => {
                const formData = new FormData();
                // Append all files with the same name 'photos' for multiple file upload
                Array.from(files).forEach(file => {
                    formData.append('photos', file);
                });
                // Don't set Content-Type header - let axios set it automatically with boundary
                return apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/photos`, null, formData);
            },
            // Get all photos for an event
            list: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/photos`),
            // Delete a single photo
            delete: (eventId, photoId) => apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/photos/${photoId}`),
            // Delete multiple photos
            deleteMultiple: (eventId, photoIds) => apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/photos`, null, photoIds),
            // Toggle visibility of a single photo
            toggleVisibility: (eventId, photoId, visible) => {
                // Spring expects JSON body with Boolean value
                // We need to ensure the Boolean is sent in the request body
                // Axios will automatically JSON.stringify when Content-Type is application/json
                return apiHelper.patch(
                    `${HOST}${EVENT_PATH}/${eventId}/photos/${photoId}/visibility`, 
                    null, 
                    visible,
                    {
                        'Content-Type': 'application/json'
                    }
                );
            },
            // Toggle visibility of multiple photos
            toggleVisibilityMultiple: (eventId, photoIds, visible) => 
                apiHelper.patch(`${HOST}${EVENT_PATH}/${eventId}/photos/visibility`, null, { photoIds, visible }, {
                    'Content-Type': 'application/json'
                }),
        },
        responsibles: {
            list: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/responsibles`),
            add: (eventId, participantId) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/responsibles/${participantId}`),
            remove: (eventId, participantId) => apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/responsibles/${participantId}`),
        },
        onboarding: {
            questions: {
                list: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions`),
                get: (eventId, questionId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}`),
                create: (eventId, body) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions`, null, body),
                update: (eventId, questionId, body) => apiHelper.put(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}`, null, body),
                delete: (eventId, questionId) => apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}`),
                getWithTranslations: (eventId, language = 'en', defaultLanguage = 'en') =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/translated`, { language, defaultLanguage }),
                getForParticipant: (eventId, language = 'en', defaultLanguage = 'en') =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/participant`, { language, defaultLanguage }),
            },
            answers: {
                submit: (eventId, body) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/onboarding/answers`, null, body),
                submitUserAnswer: (eventId, body) => apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/onboarding/user-answers`, null, body),
                getParticipantAnswer: (eventId, questionId, participantId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}/answers/${participantId}`),
                getQuestionAnswers: (eventId, questionId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}/answers`),
                getEventAnswers: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/answers`),
                getUserAnswers: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/user-answers`),
                getParticipantAnswers: (eventId, participantId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/participants/${participantId}/answers`),
                delete: (eventId, questionId, participantId) =>
                    apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}/answers/${participantId}`),
            },
            statistics: {
                getQuestionStats: (eventId, questionId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/questions/${questionId}/statistics`),
                getEventStats: (eventId) => apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/statistics`),
                checkCompletion: (eventId, participantId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/participants/${participantId}/completion`),
                getProgress: (eventId, participantId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/progress/${participantId}`),
                getAllProgress: (eventId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/progress`),
                complete: (eventId, participantId) =>
                    apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/onboarding/complete/${participantId}`),
            },
            reports: {
                getEventParticipantsReport: (eventId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/answers`),
                getAggregatedParticipantsReport: (eventId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/report`),
                getEventParticipantDetails: (eventId, participantId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/participants/${participantId}/answers`),
                getEventStatistics: (eventId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/statistics`),
                getProfileExport: (eventId, lang) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/profile-export`, lang ? { lang } : undefined),
                exportEventAnswers: (eventId, format = 'csv') =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/answers/export?format=${format}`),
            },
            translations: {
                save: (eventId, translationKey, language, translatedText) =>
                    apiHelper.post(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations`, null, {
                        translationKey,
                        language,
                        translatedText
                    }),
                getByKey: (eventId, translationKey) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations/key/${translationKey}`),
                get: (eventId, translationKey, language) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations/key/${translationKey}/language/${language}`),
                getWithFallback: (eventId, translationKey, language, defaultLanguage) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations/key/${translationKey}/language/${language}/fallback/${defaultLanguage}`),
                getAvailableLanguages: (eventId) =>
                    apiHelper.get(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations/languages`),
                delete: (eventId, translationKey, language) =>
                    apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations/key/${translationKey}/language/${language}`),
                deleteAllForKey: (eventId, translationKey) =>
                    apiHelper.del(`${HOST}${EVENT_PATH}/${eventId}/onboarding/translations/key/${translationKey}`),
            },
        },
    },
    mentorship: {
        state: (communityId) =>
            apiHelper.get(`${HOST}${MENTORSHIP_PATH}/state`, { communityId }),
        questions: (communityId, role, lang) =>
            apiHelper.get(`${HOST}${MENTORSHIP_PATH}/questions`, {
                communityId,
                role,
                ...(lang ? { lang: String(lang).toLowerCase() } : {}),
            }),
        submitAnswers: (communityId, body) =>
            apiHelper.post(`${HOST}${MENTORSHIP_PATH}/answers`, { communityId }, body),
        updateAnswers: (communityId, body) =>
            apiHelper.put(`${HOST}${MENTORSHIP_PATH}/answers`, { communityId }, body),
        switchRole: (communityId, body) =>
            apiHelper.post(`${HOST}${MENTORSHIP_PATH}/switch-role`, { communityId }, body),
        setVisibility: (communityId, body) =>
            apiHelper.patch(`${HOST}${MENTORSHIP_PATH}/visibility`, { communityId }, body),
        myAnswers: (communityId, role) =>
            apiHelper.get(`${HOST}${MENTORSHIP_PATH}/answers/me`, { communityId, role }),
        searchMentors: (communityId, params = {}) =>
            apiHelper.get(`${HOST}${MENTORSHIP_PATH}/mentors`, {
                communityId,
                ...params,
                ...(params.lang ? { lang: String(params.lang).toLowerCase() } : {}),
            }),
        mentorDetail: (communityId, profileId) =>
            apiHelper.get(`${HOST}${MENTORSHIP_PATH}/mentors/${profileId}`, { communityId }),
        menteeDetail: (communityId, profileId) =>
            apiHelper.get(`${HOST}${MENTORSHIP_PATH}/mentees/${profileId}`, { communityId }),
        favorites: {
            list: (communityId, params = {}) =>
                apiHelper.get(`${HOST}${MENTORSHIP_PATH}/favorites`, { communityId, ...params }),
            add: (communityId, mentorProfileId) =>
                apiHelper.post(`${HOST}${MENTORSHIP_PATH}/favorites/${mentorProfileId}`, { communityId }),
            remove: (communityId, mentorProfileId) =>
                apiHelper.del(`${HOST}${MENTORSHIP_PATH}/favorites/${mentorProfileId}`, { communityId }),
        },
        requests: {
            send: (communityId, body) =>
                apiHelper.post(`${HOST}${MENTORSHIP_PATH}/requests`, { communityId }, body),
            cancel: (communityId, requestId) =>
                apiHelper.del(`${HOST}${MENTORSHIP_PATH}/requests/${requestId}`, { communityId }),
            accept: (communityId, requestId) =>
                apiHelper.post(`${HOST}${MENTORSHIP_PATH}/requests/${requestId}/accept`, { communityId }),
            decline: (communityId, requestId) =>
                apiHelper.post(`${HOST}${MENTORSHIP_PATH}/requests/${requestId}/decline`, { communityId }),
            complete: (communityId, requestId) =>
                apiHelper.post(`${HOST}${MENTORSHIP_PATH}/requests/${requestId}/complete`, { communityId }),
            listIncoming: (communityId, params = {}) => {
                const query = {
                    communityId,
                    page: params.page,
                    size: params.size,
                };
                if (params.status) query.status = params.status;
                if (params.statuses?.length) {
                    query.statuses = Array.isArray(params.statuses)
                        ? params.statuses.join(',')
                        : params.statuses;
                }
                return apiHelper.get(`${HOST}${MENTORSHIP_PATH}/requests/incoming`, query);
            },
            listOutgoing: (communityId, params = {}) =>
                apiHelper.get(`${HOST}${MENTORSHIP_PATH}/requests/outgoing`, { communityId, ...params }),
        },
    },
    vacancy: {
        list: (filters) => {
            const params = {
                communityId: filters.communityId,
                page: filters.page ?? 0,
                size: filters.size ?? 12,
                includeArchived: filters.includeArchived ?? false,
            };
            if (filters.search) params.search = filters.search;
            if (filters.location) params.location = filters.location;
            if (filters.workType && filters.workType !== 'ALL') params.workType = filters.workType;
            if (filters.contractType && filters.contractType !== 'ALL') params.contractType = filters.contractType;
            if (filters.tag) params.tag = filters.tag;
            if (filters.favoritesOnly) params.favoritesOnly = true;
            return apiHelper.get(`${HOST}${VACANCY_PATH}/list`, params);
        },
        getAll: (communityId) =>
            apiHelper.get(`${HOST}${VACANCY_PATH}`, { communityId }),
        get: (vacancyId) =>
            apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}`),
        getBySlug: (slug) =>
            apiHelper.get(`${HOST}${VACANCY_PATH}/slug/${slug}`),
        archived: (communityId) =>
            apiHelper.get(`${HOST}${VACANCY_PATH}/archived`, { communityId }),
        count: (communityId) =>
            apiHelper.get(`${HOST}${VACANCY_PATH}/count`, { communityId }),
        create: (body) =>
            apiHelper.post(`${HOST}${VACANCY_PATH}/create`, null, body),
        parse: (body) =>
            apiHelper.post(`${HOST}${VACANCY_PATH}/parse`, null, body),
        improveDescription: (body) =>
            apiHelper.post(`${HOST}${VACANCY_PATH}/description/improve`, null, body),
        extractFromDescription: (body) =>
            apiHelper.post(`${HOST}${VACANCY_PATH}/description/extract`, null, body),
        update: (vacancyId, body) =>
            apiHelper.put(`${HOST}${VACANCY_PATH}/${vacancyId}`, null, body),
        delete: (vacancyId) =>
            apiHelper.del(`${HOST}${VACANCY_PATH}/${vacancyId}`),
        uploadLogo: (vacancyId, file) => {
            const formData = new FormData();
            formData.append('logo', file);
            return apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/upload-logo`, null, formData);
        },
        uploadPhoto: (vacancyId, file) => {
            const formData = new FormData();
            formData.append('photo', file);
            return apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/upload-photo`, null, formData);
        },
        filters: {
            cities: (communityId, search = '') =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/cities`, { communityId, search }),
            countries: (communityId, search = '') =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/countries`, { communityId, search }),
        },
        tags: {
            skills: (communityId, search = '') =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/tags/skills`, { communityId, search }),
            categories: (communityId, search = '') =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/tags/categories`, { communityId, search }),
            sectors: (communityId, search = '') =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/tags/sectors`, { communityId, search }),
        },
        onboarding: {
            questions: {
                list: (vacancyId) =>
                    apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/questions`),
                listForParticipant: (vacancyId) =>
                    apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/questions/participant`),
                get: (vacancyId, questionId) =>
                    apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/questions/${questionId}`),
                create: (vacancyId, body) =>
                    apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/questions`, null, body),
                update: (vacancyId, questionId, body) =>
                    apiHelper.put(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/questions/${questionId}`, null, body),
                delete: (vacancyId, questionId) =>
                    apiHelper.del(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/questions/${questionId}`),
            },
            answers: {
                submit: (vacancyId, body) =>
                    apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/answers`, null, body),
                uploadFile: (vacancyId, questionId, file) => {
                    const formData = new FormData();
                    formData.append('questionId', questionId);
                    formData.append('file', file);
                    return apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/answers/upload-file`, null, formData);
                },
            },
            progress: {
                get: (vacancyId, participantId) =>
                    apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/progress`, { participantId }),
                markCompleted: (vacancyId, participantId) =>
                    apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/onboarding/complete`, { participantId }),
            },
        },
        applications: {
            submit: (vacancyId) =>
                apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/applications`),
            getStatus: (vacancyId, participantId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/applications/status`, { participantId }),
            list: (vacancyId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/applications`),
            listDetailed: (vacancyId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/applications/details`),
            getDetailed: (vacancyId, applicationId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/applications/details/${applicationId}`),
            downloadAttachmentUrl: (vacancyId, applicationId, fileUrl) =>
                `${HOST}${VACANCY_PATH}/${vacancyId}/applications/details/${applicationId}/attachments/download?fileUrl=${encodeURIComponent(fileUrl)}`,
            updateStatus: (vacancyId, applicationId, status) =>
                apiHelper.put(`${HOST}${VACANCY_PATH}/${vacancyId}/applications/${applicationId}/status`, { status }),
            bulkUpdateStatus: (vacancyId, applicationIds, status) =>
                apiHelper.put(`${HOST}${VACANCY_PATH}/${vacancyId}/applications/bulk-status`, { status }, applicationIds),
            withdraw: (vacancyId) =>
                apiHelper.del(`${HOST}${VACANCY_PATH}/${vacancyId}/applications`),
        },
        responseTemplates: {
            list: (vacancyId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/response-templates`),
            get: (vacancyId, templateType) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/response-templates/${templateType}`),
            save: (vacancyId, templateType, body) =>
                apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/response-templates/${templateType}`, null, body),
            delete: (vacancyId, templateId) =>
                apiHelper.del(`${HOST}${VACANCY_PATH}/${vacancyId}/response-templates/${templateId}`),
        },
        responsibles: {
            list: (vacancyId, { publicOnly = false } = {}) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/responsibles`, publicOnly ? { publicOnly: true } : {}),
            current: (vacancyId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/responsibles/current`),
            add: (vacancyId, participantId, isVisible = true) =>
                apiHelper.post(
                    `${HOST}${VACANCY_PATH}/${vacancyId}/responsibles/${participantId}`,
                    { isVisible }
                ),
            updateVisibility: (vacancyId, participantId, isVisible) =>
                apiHelper.patch(
                    `${HOST}${VACANCY_PATH}/${vacancyId}/responsibles/${participantId}`,
                    null,
                    { isVisible }
                ),
            remove: (vacancyId, participantId) =>
                apiHelper.del(`${HOST}${VACANCY_PATH}/${vacancyId}/responsibles/${participantId}`),
        },
        favorites: {
            list: (communityId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/favorites`, { communityId }),
            add: (vacancyId, communityId) =>
                apiHelper.post(`${HOST}${VACANCY_PATH}/${vacancyId}/favorites`, { communityId }),
            remove: (vacancyId, communityId) =>
                apiHelper.del(`${HOST}${VACANCY_PATH}/${vacancyId}/favorites`, { communityId }),
            status: (vacancyId, communityId) =>
                apiHelper.get(`${HOST}${VACANCY_PATH}/${vacancyId}/favorite-status`, { communityId }),
        },
    },
    participantDocument: {
        upload: (communityId, documentType, file, title, description) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('documentType', documentType);
            formData.append('communityId', communityId);
            if (title) formData.append('title', title);
            if (description) formData.append('description', description);
            return apiHelper.post(`${HOST}/api/participants/documents`, null, formData);
        },
        list: (communityId) =>
            apiHelper.get(`${HOST}/api/participants/documents`, { communityId }),
        listByType: (communityId, documentType) =>
            apiHelper.get(`${HOST}/api/participants/documents/by-type`, { communityId, documentType }),
        getRelevant: (communityId) =>
            apiHelper.get(`${HOST}/api/participants/documents/relevant`, { communityId }),
        delete: (communityId, documentId) =>
            apiHelper.del(`${HOST}/api/participants/documents/${documentId}`, { communityId }),
    },
    credits: {
        // get: () => apiHelper.get(`${HOST}${API_PATH}/credits`),
        get: () => Promise.resolve({ "INTERESTED_CLICK": 10 }),
    },
    bookmarks: {
        get: (page = 0) => apiHelper.get(`${HOST}${API_PATH}/bookmarks`, { page }),
        add: (participantId) => apiHelper.post(`${HOST}${API_PATH}/bookmarks/${participantId}`),
        remove: (participantId) => apiHelper.del(`${HOST}${API_PATH}/bookmarks/${participantId}`),
    },
    relations: {
        get: (page = 0) => apiHelper.get(`${HOST}${API_PATH}/relations`, { page }),
        add: (participantId, subjectId, like, note) => apiHelper.post(`${HOST}${API_PATH}/relations`, null, {
            participantId: participantId,
            subjectId: subjectId,
            relationType: like ? "LIKE" : "DISLIKE",
            note: note
        }),
    },
    requests: {
        get: (page = 0) => apiHelper.get(`${HOST}${API_PATH}/requests`, { page }),
        answer: (requestId, answer) => apiHelper.post(`${HOST}${API_PATH}/requests/${requestId}/answer`, null, { answer }),
    },
    profile: {
        profileCompletion: (participantId) => apiHelper.get(`${HOST}${PROFILE_PATH}/${participantId}/profile-completion`),
    },
    matching: {
        matchesHistory: (page = 0, participantId) => apiHelper.get(`${HOST}${MATCHING_PATH}/matches-history`, {
            participantId,
            page,
        }),
        calcRelevanceRate: () => apiHelper.post(`${HOST}${MATCHING_PATH}/calc-relevance-rate`),
        getRelevanceRate: (participantId) => apiHelper.get(`${HOST}${MATCHING_PATH}/get-relevance-rate`, { participantId }),
        getInstantMatching: (page, status) => apiHelper.get(`${HOST}${MATCHING_PATH}/instant`, { page, status }),
        updateScore: (matchingId, score) => apiHelper.post(`${HOST}${MATCHING_PATH}/update-score`, null, {
            matchingId,
            score,
        }),
        updateNote: (matchingId, note) => apiHelper.post(`${HOST}${MATCHING_PATH}/update-note`, null, {
            matchingId,
            note,
        }),
        sendIntro: (from, matchingResultId, introMessage) => apiHelper.post(`${HOST}${MATCHING_PATH}/send-intro`, null, {
            from,
            matchingResultId,
            introMessage
        }),
        pendingScore: (participantId) => apiHelper.get(`${HOST}${MATCHING_PATH}/${participantId}/pending-score`),
        requests: (page = 0) => apiHelper.get(`${HOST}${MATCHING_PATH}/requests`, { page }),
    },
    question: {
        get: (communityId) => apiHelper.get(`${HOST}${QUESTIONS_PATH}/get-by-community`, { communityId })
    },
    images: {
        uploadLink: (imageUrl) => apiHelper.post(`${HOST}${API_PATH}/images/upload/link`, null, { imageUrl }),
        upload: (image) => apiHelper.post(`${HOST}${API_PATH}/images/upload`, null, image),
        delete: () => apiHelper.del(`${HOST}${API_PATH}/images/remove`)
    },
    landing: {
        get: (communityDomain, langSuffix = "") => apiHelper.get(`/community/${communityDomain}/landing${langSuffix.toLowerCase()}.json`, null, {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
        }),
    },
    communityBranding: {
        landing: (domain, lang) =>
            apiHelper.get(`${HOST}${API_PATH}/community/by-domain/${domain}/landing`, lang ? { lang } : undefined),
    },
    navigation: {
        get: (communityDomain) => apiHelper.get(`/community/${communityDomain}/navigation.json`, null, {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
        }),
    },
    chats: {
        getChats: (communityId, page = 0) => {
            const url = `${HOST}${API_PATH}/chats`;
            const params = { communityId, page };
            return apiHelper.get(url, params)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    console.error('[CHAT API] getChats Error:', { url, params, error });
                    throw error;
                });
        },
        createChat: (communityId, name, participants) => {
            const url = `${HOST}${API_PATH}/chats`;
            const params = { communityId, name };
            const body = { participants };
            return apiHelper.post(url, params, body)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    console.error('[CHAT API] createChat Error:', { url, params, body, error });
                    throw error;
                });
        },
        getChat: (chatId, communityId, page = 0) => {
            const url = `${HOST}${API_PATH}/chats/${chatId}`;
            const params = { communityId, page };
            return apiHelper.get(url, params)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    console.error('[CHAT API] getChat Error:', { url, params, error });
                    throw error;
                });
        },
        postMessage: (chatId, communityId, text) => {
            const url = `${HOST}${API_PATH}/chats/${chatId}`;
            const params = { communityId };
            const body = { text };
            return apiHelper.post(url, params, body)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    console.error('[CHAT API] postMessage Error:', { url, params, body, error });
                    throw error;
                });
        },
        unread: (communityId) => apiHelper.get(`${HOST}${API_PATH}/chat/unread`, { communityId }),
    },
    telegram: {
        connect: (body) => apiHelper.post(`${HOST}${API_PATH}/telegram/connect`, null, body),
        ping: (payload = {}) => apiHelper.post(`${HOST}${API_PATH}/telegram/ping`, null, payload, {
            "Content-Type": "application/json;charset=UTF-8"
        }),
        miniappInit: async (initData) => {
            // Resolve Public-Ip non-blocking with short timeout and fallback
            const resolvePublicIp = async () => {
                try {
                    const timeoutMs = 800;
                    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('ip_timeout')), timeoutMs));
                    return await Promise.race([getMyIp(), timeout]);
                } catch (e) {
                    console.warn('[VoleeApi] Public-Ip resolution failed, proceeding without it:', e?.message || e);
                    return 'unknown';
                }
            };

            const publicIp = await resolvePublicIp();
            const headers = {
                "Content-Type": "text/plain;charset=UTF-8",
                "Public-Ip": publicIp,
                "Community": getDomain()
                // Note: We intentionally do NOT include Authorization header
            };

            const url = `${HOST}${API_PATH}/telegram/miniapp/init`;
            console.log('[VoleeApi] Calling miniappInit:', {
                url: url,
                initDataLength: initData?.length,
                headers
            });

            try {
                console.log('[VoleeApi] Making POST request to:', url);
                const response = await axios({
                    method: 'post',
                    url: url,
                    data: initData, // Send as raw string
                    headers: headers
                });
                console.log('[VoleeApi] miniappInit response:', response.data);
                return response.data;
            } catch (error) {
                console.error('[VoleeApi] miniappInit error:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data,
                    headers: error.response?.headers
                });
                // Re-throw with proper error format
                const parsedError = {
                    code: error.response?.data?.code || error.response?.status || 1000,
                    msg: error.response?.data?.message || error.response?.data?.error || error.message || 'auto_login_failed',
                    response: error.response
                };
                throw parsedError;
            }
        },
    },
    permission: {
        my: () => apiHelper.get(`${HOST}${API_PATH}/permission/my`),
    },
    admin: {
        nextMatch: (communityId) => apiHelper.get(`${HOST}${ADMIN_PATH}/${communityId}/matches/next`),
        history: (pairId) => apiHelper.get(`${HOST}${ADMIN_PATH}/pairs/${pairId}/history`),
        assistance: (pairId) => apiHelper.get(`${HOST}${ADMIN_PATH}/pairs/${pairId}/score-assistance`),
        setScore: (pairId, internalAdminScore) => apiHelper.post(`${HOST}${ADMIN_PATH}/pairs/${pairId}/score`, { internalAdminScore }),
        activeParticipantsCSV: (communityId) => apiHelper.get(`${HOST}${ADMIN_PATH}/stats/community/${communityId}/participants`),
        participants: (communityId, page, search, profiles) => apiHelper.get(`${HOST}${ADMIN_PATH}/${communityId}/participants`, { page, search, profiles }),
        notify: (participantId, body) => apiHelper.post(`${HOST}${ADMIN_PATH}/participants/${participantId}/notify`, null, body),
        notifyTelegram: (participantId, formData) => apiHelper.post(`${HOST}${ADMIN_PATH}/participants/${participantId}/notify/telegram`, null, formData),
        toggleProfile: (participantId, shouldDisable) => apiHelper.post(`${HOST}${ADMIN_PATH}/participants/${participantId}/profile/toggle`, { shouldDisable }),
        toggleNetwork: (participantId, shouldDisable) => apiHelper.post(`${HOST}${ADMIN_PATH}/participants/${participantId}/networking/toggle`, { shouldDisable }),
        /**
         * Bulk-disable networking for participants in a community, based on filters.
         * Currently it supports disabling networking for all members without connected Telegram.
         */
        disableNetworkingWithoutTelegram: (communityId) =>
            apiHelper.post(`${HOST}${ADMIN_PATH}/${communityId}/participants/networking/disable-by-filters`, {
                withoutTelegramOnly: true,
            }),
        enableNetworkingForAll: (communityId) => apiHelper.post(`${HOST}${ADMIN_PATH}/${communityId}/participants/networking/enable-all`, null, null),
    },
    participantModeration: {
        getParticipants: (communityId, page = 0, size = 20, search = '', status = '') => apiHelper.get(`${HOST}${API_PATH}/participant-moderation/community/${communityId}`, { page, size, search, status }),
        updateStatus: (participantId, status) => apiHelper.patch(`${HOST}${API_PATH}/participant-moderation/${participantId}`, null, { moderationStatus: status }),
        getModerators: (communityId) => apiHelper.get(`${HOST}${API_PATH}/participant-moderation/community/${communityId}/moderators`)
    },
    constants: {
        contacts: {
            types: () => apiHelper.get(`${HOST}${API_PATH}/constants/contacts/types`)
        }
    }
};
